import React, { useState } from 'react'
import { Mail, Bell, Check, AlertCircle, Loader2, Info } from 'lucide-react'
import { useWaitlist } from '../hooks/useWaitlist'

interface WaitlistFormProps {
  source?: string
  onSuccess?: () => void
  className?: string
}

export default function WaitlistForm({ source = 'website', onSuccess, className = '' }: WaitlistFormProps) {
  const [email, setEmail] = useState('')
  const { isLoading, isSubmitted, error, message, submitEmail } = useWaitlist()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    await submitEmail(email, source)
    
    if (onSuccess) {
      onSuccess()
    }
    
    // Clear email on success
    if (!error) {
      setEmail('')
    }
  }

  if (isSubmitted) {
    return (
      <div className={`text-center ${className}`}>
        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
        </div>
        <h3 className="font-poppins font-bold text-2xl sm:text-3xl lg:text-4xl text-white mb-6">
          {message?.includes('already') ? "You're Already In!" : "Welcome to the Revolution!"}
        </h3>
        <p className="font-inter text-slate-300 mb-8 text-lg sm:text-xl">
          {message || "You're now on the UNDRESSING waitlist. Get ready to strip the bland and drip the bold!"}
        </p>
        <div className="space-y-4 text-slate-400">
          <div className="flex items-center justify-center space-x-3">
            <Check className="h-5 w-5 sm:h-6 sm:w-6 text-green-400" />
            <span className="font-medium">You'll be first to know about our launch</span>
          </div>
          <div className="flex items-center justify-center space-x-3">
            <Check className="h-5 w-5 sm:h-6 sm:w-6 text-green-400" />
            <span className="font-medium">Exclusive early access pricing</span>
          </div>
          <div className="flex items-center justify-center space-x-3">
            <Check className="h-5 w-5 sm:h-6 sm:w-6 text-green-400" />
            <span className="font-medium">Free shipping on your first order</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Mail className="absolute left-4 sm:left-5 top-1/2 transform -translate-y-1/2 h-5 w-5 sm:h-6 sm:w-6 text-slate-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full pl-12 sm:pl-14 pr-4 sm:pr-6 py-3 sm:py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-pink-500 focus:bg-white/15 transition-all duration-300 text-base sm:text-lg disabled:opacity-50"
              required
              disabled={isLoading}
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || !email}
            className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 sm:px-8 py-3 sm:py-4 font-poppins font-bold hover:shadow-xl hover:shadow-pink-500/25 transition-all duration-300 hover:scale-105 rounded-xl flex items-center justify-center space-x-3 text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 min-h-[52px] sm:min-h-[60px]"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 animate-spin" />
                <span>Joining...</span>
              </>
            ) : (
              <>
                <Bell className="h-5 w-5 sm:h-6 sm:w-6" />
                <span>Join Waitlist</span>
              </>
            )}
          </button>
        </div>
        
        {error && (
          <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center space-x-3">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <p className="text-red-300">{error}</p>
          </div>
        )}

        {message && !isSubmitted && (
          <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center space-x-3">
            <Info className="h-5 w-5 text-blue-400 flex-shrink-0" />
            <p className="text-blue-300">{message}</p>
          </div>
        )}
      </form>
    </div>
  )
}